import React, { useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useDialog } from "../../DialogContext";
import CreateCommentForm from "./CreateCommentForm";
import CommentFeedPanel from "./CommentFeedPanel";
import { getCommentModuleDefinition } from "./commentActionUtils";

const panelSurfaceSx = {
  borderRadius: "18px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
  boxShadow: "0 16px 34px rgba(15, 23, 42, 0.06)",
};

const RecordNotesPanel = ({
  module,
  objectId,
  objectName,
  entityType,
  dialogTitle,
  variant = "embedded",
}) => {
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const { showDialog } = useDialog();
  const [refreshSignal, setRefreshSignal] = useState(0);

  const resolvedEntityType = useMemo(() => {
    if (entityType) return entityType;
    return getCommentModuleDefinition(module)?.entityType || module;
  }, [entityType, module]);

  const handleAddNote = () => {
    showDialog(
      <CreateCommentForm
        module={module}
        object_id={objectId}
        object_name={objectName}
        defaultEntryType="note"
        lockEntryType
        onSuccess={() => setRefreshSignal((current) => current + 1)}
      />,
      {
        dialogTitle: dialogTitle || "Add Note",
      },
    );
  };

  return (
    <Box
      sx={{
        ...panelSurfaceSx,
        p: variant === "dialog" ? 0 : { xs: 2, md: 2.5 },
      }}
    >
      <Box
        sx={{
          px: variant === "dialog" ? { xs: 2, md: 2.5 } : 0,
          pt: variant === "dialog" ? { xs: 2, md: 2.5 } : 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Private notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Notes stay visible only to you and stay attached to this record.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRounded />}
          onClick={handleAddNote}
          sx={{ textTransform: "capitalize" }}
        >
          Add Note
        </Button>
      </Box>

      <Box sx={{ px: variant === "dialog" ? { xs: 2, md: 2.5 } : 0, pb: variant === "dialog" ? { xs: 2, md: 2.5 } : 0 }}>
        <CommentFeedPanel
          token={token}
          teamMemberLawyerId={teamMemberLawyerId}
          feedType="entity"
          entityType={resolvedEntityType}
          entityId={objectId}
          entryTypeFilter="note"
          title="Notes"
          hideModuleFilters
          refreshSignal={refreshSignal}
          emptyStateTitle="No private notes yet"
          emptyStateDescription="Add a note to keep a personal reminder on this record without sharing it with the rest of the firm."
        />
      </Box>
    </Box>
  );
};

export default RecordNotesPanel;
